/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import _get from 'lodash/get'
import classNames from 'classnames/bind'
import { SUBSCRIPTION_STATUS, SUBSCRIPTION_PLAN_TYPE } from 'constants/subscription'
import { Text } from 'hocs/localization'
import LocalizationText from 'apps/LocalizationText'
import Button from 'elements/Button'
import { getPrintedDateLocal, isContractExpired } from 'utils/date'
import { getFormattedTextOrDefault } from 'utils/printedData'
import { CONFIRM_CANCEL_SUBSCRIPTION_MODAL_NAME } from './contants'
import ConfirmCancelSubscriptionModal from './apps/ConfirmCancelSubscriptionModal'
import * as styles from './ManageAccount.scss'
import ConfirmChangingPlanModal from '../ConfirmChangingPlanModal'
import ForbiddenChangingPlanModal from '../ForbiddenChangingPlanModal'
import { EMPTY_OBJECT } from 'constants/frozenObjects'

const cx = classNames.bind(styles)

interface IProps {
  t: Text
  openChangingConfirm: () => void,
  isUserAbleToChangeSubscription: boolean,
  userSubscription: any,
  futureSubscription: any,
  onOpenCancellationConfirm: () => void,
  onGoToRenewalProcess: () => void,
  onGoToChangePlanProcess: () => void,
  onGoToEnrollPlanProcess: () => void,
  isUserAbleToCancelSubscription: boolean,
  isUserConfirmedEmail: boolean,
  isUserUnableToUpdateInfo: boolean,
  isEnrollAvailable: boolean,
  isUserContractExpired: boolean,
  isChangeSubscriptionAllowed: boolean,
  subscriptionStatus: SUBSCRIPTION_STATUS,
}

const getPrintedStatus = (subscriptionStatus, t) => {
  let outputStatus = subscriptionStatus
  if (
    subscriptionStatus === SUBSCRIPTION_STATUS.pendingLocked ||
    subscriptionStatus === SUBSCRIPTION_STATUS.pendingPayment ||
    subscriptionStatus === SUBSCRIPTION_STATUS.locking
  ) {
    outputStatus = SUBSCRIPTION_STATUS.activated
  }

  return t('details.status', { status: t(`subscriptionStatuses.${outputStatus}`) })
}

const ManageAccount: React.FC<IProps> = ({
  t,
  openChangingConfirm,
  userSubscription,
  futureSubscription,
  isUserAbleToChangeSubscription,
  onOpenCancellationConfirm,
  onGoToRenewalProcess,
  onGoToChangePlanProcess,
  onGoToEnrollPlanProcess,
  isUserAbleToCancelSubscription,
  isUserConfirmedEmail,
  isUserUnableToUpdateInfo,
  subscriptionStatus,
  isEnrollAvailable,
  isChangeSubscriptionAllowed,
  isUserContractExpired
}) => {
  const termStart = _get(userSubscription, 'termsStartDate')
  const termEnd = _get(userSubscription, 'termsEndDate')
  const hasDebts = _get(userSubscription, 'hasDebts')
  const contractType = _get(userSubscription, 'subscriptionContractType')
  const isCurrentContractExpired = futureSubscription === EMPTY_OBJECT && isEnrollAvailable

  return (
    <div className={cx('fieldsContainer', 'text-center')}>
      <ul className={styles.details}>
        <li className={styles.detailsItem}>
          <LocalizationText>
            {t('details.termStart', { termStart: isUserContractExpired ? 'N/A'
            : getFormattedTextOrDefault(getPrintedDateLocal)(termStart) })}
          </LocalizationText>
        </li>
        <li className={styles.detailsItem}>
          <LocalizationText>
            {t('details.termEnd', { termEnd: isUserContractExpired ? 'N/A'
            : getFormattedTextOrDefault(getPrintedDateLocal)(termEnd) })}
          </LocalizationText>
        </li>
        <li className={styles.detailsItem}>
          <LocalizationText>
            {t('details.subscriptionType', { subscriptionType: contractType === SUBSCRIPTION_PLAN_TYPE.contract ? 'Contract' : 'Month-2-Month' })}
          </LocalizationText>
        </li>
        {isCurrentContractExpired && isUserContractExpired && (
          <li className={styles.detailsItem}>
            Subscription Status:<strong><li className={styles.expiredStatus}>Expired</li></strong>
          </li>
        )}
        {!isUserContractExpired && (
          <li className={styles.detailsItem}>
            <LocalizationText>
              {getPrintedStatus(subscriptionStatus, t)}
            </LocalizationText>
        </li>
        )}
      </ul>

      {!isCurrentContractExpired && (
        <div className={styles.formFieldWrapper}>
          {isChangeSubscriptionAllowed && (
            <div className={styles.formField}>
              <div>
                <Button
                  disabled={!isUserAbleToChangeSubscription ||
                            isUserUnableToUpdateInfo ||
                            hasDebts}
                  onClick={openChangingConfirm}
                >
                  {t('changeButtonTitle')}
                </Button>
                <p className={styles.Noted}>{t('note')}</p>
              </div>
            </div>
          )}
          <div className={styles.formField}>
            <div>
              <Button
                styleType='red'
                onClick={onOpenCancellationConfirm}
                disabled={!isUserAbleToCancelSubscription || isUserUnableToUpdateInfo || hasDebts}
              >
                {t('cancelButtonTitle')}
              </Button>
              <p className={styles.Noted}>{t('note.cancel')}</p>
            </div>
          </div>
        </div>
      )}

      {isCurrentContractExpired && (
        <div className={styles.formFieldWrapper}>
          <div className={styles.formFieldEnroll}>
            <div>
              <Button
                onClick={onGoToEnrollPlanProcess}
              >
                {t('enrollButtonTitle')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmChangingPlanModal/>
      <ForbiddenChangingPlanModal/>
      <ConfirmCancelSubscriptionModal name={CONFIRM_CANCEL_SUBSCRIPTION_MODAL_NAME} />      
    </div>
  )
}

export default ManageAccount
